import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type Phase = 'loading' | 'ok' | 'error' | 'missing';

function SecurityBlockPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [phase, setPhase] = useState<Phase>('loading');
  const [message, setMessage] = useState<string>('');

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token?.trim()) {
      setPhase('missing');
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(`${API_BASE}/auth/security/intrusion-block`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: token.trim() }),
        });
        const body = (await res.json().catch(() => ({}))) as {
          message?: string | string[];
        };
        if (cancelled) return;
        if (!res.ok) {
          const raw = body.message;
          const msg = Array.isArray(raw) ? raw[0] : raw;
          setMessage(
            typeof msg === 'string' && msg.trim()
              ? msg
              : t('auth.security_block_error_generic'),
          );
          setPhase('error');
          return;
        }
        setPhase('ok');
      } catch {
        if (!cancelled) {
          setMessage(t('auth.security_block_error_generic'));
          setPhase('error');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [searchParams, t]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 px-4 py-12">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        <h1 className="text-xl font-semibold text-slate-900">
          {t('auth.security_block_title')}
        </h1>
        {phase === 'loading' ? (
          <p className="mt-4 text-sm text-slate-600">{t('auth.security_block_loading')}</p>
        ) : null}
        {phase === 'ok' ? (
          <p className="mt-4 text-sm text-emerald-800">{t('auth.security_block_success')}</p>
        ) : null}
        {phase === 'missing' ? (
          <p className="mt-4 text-sm text-slate-600">{t('auth.security_block_missing_token')}</p>
        ) : null}
        {phase === 'error' ? (
          <p className="mt-4 text-sm text-red-700">{message}</p>
        ) : null}
        <Link
          to="/auth"
          className="mt-6 inline-block text-sm font-semibold text-brand-600 hover:text-brand-500"
        >
          {t('auth.security_block_back_sign_in')}
        </Link>
      </div>
    </div>
  );
}

export default SecurityBlockPage;
